import React, { useEffect, useState, useCallback }  from 'react';
import { Alert, Row, Badge, FormFeedback, Container, Col, Button, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Link } from 'react-router-dom';
import { withFormik } from 'formik';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';
import * as Yup from 'yup';
import isilogo from './images/ISI_LOGO_2.jpg';
import 'bootstrap/dist/css/bootstrap.min.css';

const scrollToTop = () => {
  if (typeof window !== 'undefined' && window.scrollTo) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

// Max associated birthday
var MAXBIRTHDAY = new Date();
MAXBIRTHDAY.setFullYear( MAXBIRTHDAY.getFullYear() - 12 );

const MyInnerForm = props => {
  const {
    values,
    touched,
    errors,
    dirty,
    status,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleReset,
    setFieldValue,
    setStatus,
  } = props;

  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);

  const { executeRecaptcha } = useGoogleReCaptcha();
  // Stato per gestire il timestamp del token e la sua validità
  const [tokenTimestamp, setTokenTimestamp] = useState(null);
  const TOKEN_VALIDITY_DURATION = 110000; // 110 secondi

  const handleSubmitWithRecaptcha = async (event) => {
    event.preventDefault();

    if (!executeRecaptcha) {
      console.error('reCAPTCHA non è pronto');
      setStatus({ res: false, msg: 'Errore reCAPTCHA. Riprova più tardi.' });
      scrollToTop();
      return;
    }

    try {
      const now = Date.now();

      // Controlla se il token è ancora valido
      if (!values.recaptcha || !tokenTimestamp || now - tokenTimestamp >= TOKEN_VALIDITY_DURATION) {
        console.log('Token reCAPTCHA scaduto, generazione di un nuovo token...');
        const token = await executeRecaptcha('submit');
        setFieldValue('recaptcha', token);
        setTokenTimestamp(now); // Aggiorna il timestamp
      }

      // Procedi con l'invio del modulo
      handleSubmit(event);
    } catch (error) {
      console.error('Errore durante la verifica reCAPTCHA:', error);
      setStatus({ res: false, msg: 'Errore durante la verifica reCAPTCHA. Riprova.' });
    }
  };

  const ReCaptchaField = ({ setFieldValue, values }) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
  
    const handleReCaptchaVerify = useCallback(async () => {
      const now = Date.now();
  
      // Controlla se il token è ancora valido
      if (values.recaptcha && tokenTimestamp && now - tokenTimestamp < TOKEN_VALIDITY_DURATION) {
        console.log('Token reCAPTCHA ancora valido, nessuna nuova chiamata necessaria.');
        return;
      }
  
      if (!executeRecaptcha) {
        console.error('reCAPTCHA non è pronto');
        return;
      }
  
      try {
        const token = await executeRecaptcha('submit');
        setFieldValue('recaptcha', token); // Aggiorna il valore in Formik
        setTokenTimestamp(now); // Aggiorna il timestamp
        console.log('Token reCAPTCHA aggiornato');
      } catch (error) {
        console.error('Errore durante la generazione del token reCAPTCHA:', error);
      }
    }, [executeRecaptcha, setFieldValue, values.recaptcha]);
  
    // Genera il token solo una volta al montaggio del componente
    useEffect(() => {
      handleReCaptchaVerify();
    }, [handleReCaptchaVerify]);
  
    return null;
  };

  return (
    <Container className="border">    
      <Row>
        <Col xs="auto">
          <img className="isilogo" src={isilogo} width="120" alt="Logo Il sorriso di Isa"/>      
        </Col>
        <Col className="d-flex align-items-center">
          <h2>Modulo di iscrizione Il sorriso di Isa ETS</h2>
        </Col>
      </Row>
      <form className="m-3" onSubmit={handleSubmitWithRecaptcha} autoComplete="off" spellCheck="false">
      <Alert color="success" isOpen={status?.res ? true : false}>
        {status?.msg || ''}
      </Alert>
      <Alert color="danger" isOpen={status && !status.res ? true : false}>
        {status?.msg || ''}
        <p>Per i rinnovi occorre utilizzare la form di rinnovo: <Link to="/rinnovo">Rinnova</Link></p>
      </Alert>
      <FormGroup row>
          <Col xs="12" sm="6">
          <Label for="nome">Nome</Label>
          <Input
            id="nome"
            type="text"
            value={values.nome}
            className="text-capitalize"
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.nome && touched.nome}
            invalid={errors.nome && touched.nome}
          />
          <FormFeedback>{errors.nome}</FormFeedback>
          </Col>
          <Col xs="12" sm="6">
          <Label for="cognome">Cognome</Label>
          <Input
            id="cognome"
            type="text"
            className="text-capitalize"
            value={values.cognome}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.cognome && touched.cognome}
            invalid={errors.cognome && touched.cognome}
          />
          <FormFeedback>{errors.cognome}</FormFeedback>
          </Col>
      </FormGroup>

      <FormGroup row>
        <Col xs="12" sm="6">  
          <Label for="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.email && touched.email}
            invalid={errors.email && touched.email}
           />
          <FormFeedback>{errors.email}</FormFeedback>
        </Col>  
        <Col xs="12" sm="6">  
          <Label for="emailConferma">Conferma Email</Label>
          <Input
            id="emailConferma"
            type="email"
            value={values.emailConferma}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.emailConferma && touched.emailConferma}
            invalid={errors.emailConferma && touched.emailConferma}
          />
          <FormFeedback>{errors.emailConferma}</FormFeedback>
        </Col>  
      </FormGroup>

      <FormGroup row>
      <Col xs="12" sm="12" md="4">
          <Label for="telefono">Telefono</Label>
          <Input
            id="telefono"
            type="text"
            value={values.telefono}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.telefono && touched.telefono}
            invalid={errors.telefono && touched.telefono}
          />
          <FormFeedback>{errors.telefono}</FormFeedback>
       </Col>   
      <Col xs="12" sm="12" md="4">
          <Label for="data_nascita">Data Nascita</Label>
          <Input
            id="data_nascita"
            placeholder="dd/mm/aaaa"
            type="date"
            value={values.data_nascita}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.data_nascita && touched.data_nascita}
            invalid={errors.data_nascita && touched.data_nascita}
           />
          <FormFeedback>{errors.data_nascita}</FormFeedback>
      </Col>
      <Col xs="12" sm="12" md="4">  
          <Label for="luogo_nascita">Luogo di Nascita</Label>
          <Input
            id="luogo_nascita"
            type="text"
            value={values.luogo_nascita}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.luogo_nascita && touched.luogo_nascita}
            invalid={errors.luogo_nascita && touched.luogo_nascita}
           />
          <FormFeedback>{errors.luogo_nascita}</FormFeedback>
        </Col>  
      </FormGroup>

      <FormGroup row>
        <Col>  
          <Label for="indirizzo">Indirizzo</Label>
          <Input
            id="indirizzo"
            type="text"
            value={values.indirizzo}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.indirizzo && touched.indirizzo}
            invalid={errors.indirizzo && touched.indirizzo}
           />
          <FormFeedback>{errors.indirizzo}</FormFeedback>
        </Col>  
      </FormGroup>

      <FormGroup row>
      <Col xs="12" sm="12" md="4">      
          <Label for="cap">CAP</Label>
          <Input
            id="cap"
            type="text"
            value={values.cap}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.cap && touched.cap}
            invalid={errors.cap && touched.cap}
           >
         </Input>
          <FormFeedback>{errors.cap}</FormFeedback>
        </Col>      
        <Col xs="12" sm="12" md="4">      
          <Label for="codice_fiscale" translate="no">Codice Fiscale</Label>
          <Input
            id="codice_fiscale"
            type="text"
            className="text-uppercase"
            autoComplete="off"
            spellCheck="false"
            value={values.codice_fiscale}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.codice_fiscale && touched.codice_fiscale}
            invalid={errors.codice_fiscale && touched.codice_fiscale}
           >
         </Input>
          <FormFeedback>{errors.codice_fiscale}</FormFeedback>
        </Col>      
        <Col xs="12" sm="12" md="4">      
          <Label for="importo">Importo</Label>
          <Input
            id="importo"
            type="select"
            value={values.importo}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.importo && touched.importo}
            invalid={errors.importo && touched.importo}
           >
           <option value="">Scegli</option>
           <option value="55">55 euro (ordinario)</option>
           <option value="60">60 euro (sostenitore)</option>
         </Input>
        </Col>      
       </FormGroup>
      <FormGroup row>
        <Col>  
          <Label for="file_pagamento">Bonifico</Label>
          <Input
            id="file_pagamento"
            type="file"
            value={values.bonifico}
            onBlur={handleBlur}
            valid={!errors.file_pagamento && touched.file_pagamento}
            invalid={(errors.file_pagamento && touched.file_pagamento) ||
                    (values.file_pagamento && values.file_pagamento.size > (10 * 1024 * 1024))}
            onChange={(event) => {
              setFieldValue('file_pagamento', event.currentTarget.files[0]);
            }} />
        </Col>  
      </FormGroup>
      <FormGroup row>
        <Col>  
          <Label for="note">Note</Label>
          <Input
            id="note"
            type="text"
            value={values.note}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={!errors.note && touched.note}
            invalid={errors.note && touched.note}
           />
          <FormFeedback>{errors.note}</FormFeedback>
        </Col>  
      </FormGroup>
      <FormGroup switch>
        <Col className="d-flex justify-content-end">  
          <div className="mr-2">
            <Input 
            id="condizioni"
            type="checkbox" 
            checked={values.condizioni}
            onChange={handleChange}
            onBlur={handleBlur}            
            valid={!errors.condizioni && touched.condizioni}
            invalid={errors.condizioni && touched.condizioni}
            />
          </div>  
          <Label for="condizioni" check>Accetto le <Badge color="warning" onClick={toggle}>condizioni</Badge></Label>
        </Col>
      </FormGroup>
      <ReCaptchaField setFieldValue={setFieldValue} values={values} />        
        <Input
          type="hidden"
          name="recaptcha"
          value={values.recaptcha}
          onChange={handleChange}
          valid={!errors.recaptcha && touched.recaptcha}
          invalid={errors.recaptcha && touched.recaptcha}
        />
      <Button onClick={handleSubmit} disabled={isSubmitting} color="primary">
          Inserisci
      </Button>{' '}
      <Button onClick={handleReset} disabled={!dirty || isSubmitting} color="secondary">
          Cancella
      </Button>
    </form>
    <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>Informativa trattamento dati personali</ModalHeader>
        <ModalBody>
Informativa ai sensi e per gli effetti dell’art. 13 del D.Lgs. 196/2003 ed art. 14 del GDPR UE 2016/679 relativi alla tutela del trattamento dei dati personali

Ai sensi dell’art 13 del D. Lgs. 196/2003 e dell’art. 14 del GDPR UE 2016/679 relativi alla tutela del trattamento dei dati personali, ed in relazione ai dati di cui l’Associazione IL SORRISO DI ISA- ETS è in possesso e di cui verrà in possesso, Ti forniamo le seguenti informazioni. L’ Associazione è impegnata ad effettuare ogni trattamento di dati personali nel rispetto dei principi di correttezza, di liceità e trasparenza, tutelando la Tua riservatezza e Tuoi diritti.

Tipologie di dati raccolti
a) I dati personali comuni raccolti e trattati dalla scrivente rientrano nella categoria dei dati identificativi (in particolare a titolo esemplificativo e non esaustivo: nominativo, indirizzo, numero di telefono, codice fiscale, età, sesso, luogo e data di nascita, indirizzo e-mail);
b) i dati particolari ex art. 9 REG. UE 679/2016 raccolti e trattati dalla scrivente.

Finalità del trattamento
Trattiamo i Tuoi dati anagrafici, il Tuo indirizzo, anche di posta elettronica, e numero telefonico ed i dati relativi alle modalità di pagamento della quota associativa/donazione (coordinate bancarie) ed ogni altro dato personale, necessario al perseguimento dello scopo associativo ed in particolare per la gestione del rapporto associativo: iscrizione nel libro soci, eventuale accensione di polizza assicurativa, invio di comunicazioni e di materiale informativo sull’attività dell’Associazione.

Modalità del trattamento
Il trattamento dei dati avviene mediante l’utilizzo di strumenti e procedure idonei a garantirne la sicurezza e la riservatezza e potrà essere effettuato sia mediante supporti cartacei, sia con l’ausilio di mezzi informatici.

Obbligo o facoltà di conferire i dati
Per quanto concerne i dati che siamo obbligati a conoscere, al fine di adempiere agli obblighi previsti da leggi, il loro mancato conferimento da parte Vostra comporta l’impossibilità di instaurare o proseguire il rapporto associativo, nei limiti in cui tali dati sono necessari all’esecuzione dello stesso.

Comunicazione e diffusione
I dati personali non potranno essere diffusi in alcun modo salvo che ciò non corrisponda ad un preciso obbligo di legge o ad un ordine di autorità. Tuttavia i dati anagrafici, limitatamente al nome e cognome, potranno essere eventualmente pubblicati sul sito internet dell’Associazione e/o su brochure e periodici cartacei al solo fine di promuovere la cultura della prevenzione e diffondere informazioni sulle iniziative dell’Associazione stessa. I dati potranno essere comunicati ad enti pubblici, o ad eventuali compagnie di assicurazione. Potranno venire a conoscenza dei Tuoi dati, in qualità di incaricati del trattamento o responsabili, i seguenti soggetti:
- altri enti del terzo settore o enti pubblici con cui l’associazione collabori per la realizzazione di specifici progetti.
- eventuali compagnie di assicurazione
- enti pubblici cui siamo tenuti per legge o convenzione a conferire i dati.

I dati personali possono essere trasferiti verso Paesi dell’Unione Europea e verso Paesi terzi nell’ambito delle finalità sopra evidenziate.

Diritti dell’interessato
Ai sensi degli artt. dal 15 al 22 del GDPR UE 679/2016 Ti vengono conferiti specifici diritti, il cui testo completo è consultabile presso la sede dell’Associazione. In particolare puoi esercitare nei confronti dell’Associazione i seguenti diritti:
- ottenere la conferma dell’esistenza di dati personali che Ti riguardano, e la comunicazione in forma intelligibile;
- ottenere l’indicazione dell’origine dei dati personali, nonché della finalità e modalità del trattamento;
- ottenere l’indicazione della logica applicata nei trattamenti effettuati con l’ausilio di strumenti elettronici; - ottenere l’aggiornamento, la rettifica, ovvero quando di interesse, l’integrazione dei dati;
- ottenere la cancellazione, la trasformazione in forma anonima o il blocco dei dati trattati in violazione di legge;          
- ottenere la cancellazione, la trasformazione in forma anonima o il blocco dei dati di cui non è necessaria la conservazione, in relazione agli scopi per i quali i dati sono stati raccolti o successivamente trattati;
- ottenere l’attestazione che l’aggiornamento, la rettifica, l’integrazione, la cancellazione, la trasformazione in forma anonima o il blocco sono stati portati a conoscenza, anche per quanto riguarda il contenuto, di coloro ai quali i dati sono stati comunicati o diffusi, tranne che nei casi in cui tale adempimento si riveli impossibile o comporti un impiego di mezzi manifestamente sproporzionato rispetto al diritto tutelato;
- opporti in tutto o in parte, per motivi legittimi, al trattamento dei dati personali che Ti riguardano, ancorché pertinenti allo scopo della raccolta;
- opporti, in tutto o in parte, al trattamento di dati personali che Ti riguardano al fine di invio di materiale pubblicitario. Per esercitare tali diritti puoi rivolgerti all’Associazione, nella persona del suo Presidente.

Titolare del trattamento
Il Titolare del trattamento dei dati è l’Associazione IL SORRISO DI ISA - ETS C.F. 97804350011, con sede legale in Torino Via Moretta n. 7, e-mail info@ilsorrisodiisaets.org pec: ilsorrisodiisa@pec.it, tel. 345/83.88.127.
Per esercitare i diritti previsti dalla normativa vigente e sopra elencati, l’Interessato dovrà rivolgere richiesta scritta mediante i suddetti canali.
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggle}>
            Chiudi
          </Button>{' '}
        </ModalFooter>
      </Modal>    
    </Container>
   );
};

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const ModuloIscrizione = withFormik({
  mapPropsToValues: () => ({ nome:'',cognome:'',telefono:'',email: '', emailConferma: '',
                            importo:'55',data_nascita: '', note: '',
                            luogo_nascita:'', indirizzo:'', cap:'', 
                            codice_fiscale:'', condizioni: false, 
                            file_pagamento: null,
                            recaptcha: '',                         
                           }),
  validationSchema: Yup.object().shape({
    nome: Yup.string()
      .required(' ')
      .max(100, 'il nome può contenere al massimo 100 caratteri').nullable(),      
    cognome: Yup.string()
      .required(' ')
      .max(100, 'il cognome può contenere al massimo 100 caratteri').nullable(),      
    telefono: Yup.string()
      .required(' ')
      .matches(/^[0-9]{5,10}$/,'Numero di telefono non valido'),
    email: Yup.string()
      .trim()
      .matches(emailRegex, 'Inserisci un indirizzo email valido')
      .email('Formato email non corretto') // Yup ha già una validazione base
      .max(255, 'L\'email non può superare i 255 caratteri')
      .required('L\'email è obbligatoria'),
    emailConferma: Yup.string()
      .oneOf([Yup.ref('email'), null], 'Le email non coincidono')
      .required('Conferma la tua email'),      
    data_nascita: Yup.date()
      .required(' ')
      .max(MAXBIRTHDAY,'Data nascita non valida: l\'età minima è di 13 anni'),
    importo: Yup.number()
      .required(' '),
    indirizzo: Yup.string()
      .required(' ')
      .max(100, 'L\'indirizzo può contenere al massimo 100 caratteri').nullable(),      
    cap: Yup.string()
      .required(' ')
      .max(5, 'Il codice postale può contenere al massimo 5 caratteri').nullable(),      
    luogo_nascita: Yup.string()
      .required(' ')
      .max(100, 'il luogo di nascita può contenere al massimo 100 caratteri').nullable(),      
    note: Yup.string()
      .max(100, 'Le note devono avere al massimo 100 caratteri').nullable(),      
    codice_fiscale: Yup.string()
      .required(' ')
      .matches(/^(?:[A-Z][AEIOU][AEIOUX]|[AEIOU]X{2}|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i,
       'Codice fiscale non valido').nullable(),      
    file_pagamento: Yup.mixed()
      .required('Il campo Bonifico è obbligatorio')
      .test('fileSize', 'La dimensione massima consentita è di 10MB', (value) => {
        if (value) {
          const maxSize = 10 * 1024 * 1024; // 10 MB
          return value.size <= maxSize;
        }
        return true;
      }),
    condizioni: Yup.bool()
      .oneOf([true], 'Devi accettare le condizioni'),       
   }),
  handleSubmit: (values, { setStatus, setSubmitting, resetForm, setErrors, setFieldValue}) => {
    const formData = new FormData();
    formData.append('nome', values.nome);
    formData.append('cognome', values.cognome);
    formData.append('data_nascita', values.data_nascita);
    formData.append('luogo_nascita', values.luogo_nascita);
    formData.append('indirizzo', values.indirizzo);
    formData.append('cap', values.cap);
    formData.append('telefono', values.telefono);
    formData.append('email', values.email);
    formData.append('note', values.note);
    formData.append('codice_fiscale', values.codice_fiscale);
    formData.append('importo', values.importo);
    formData.append('file_pagamento', values.file_pagamento);
    formData.append('g-recaptcha-response', values.recaptcha);

    axios.post(process.env.REACT_APP_BASE_SERVER_URI+'/site/insrichiestaiscrizione', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => {
      if (response.status !== 201) {
        response.text()
        .then(msg=>{
          var errmsg = "Si è verificato un problema durante l'inserimento della richiesta.";
          if(msg){
            errmsg += " " + msg; 
          }
          setStatus({res: false, msg: errmsg});
          scrollToTop();
          setFieldValue('recaptcha', '');
        });
      }else{
        resetForm();
        document.getElementById("file_pagamento").value = "";
        setStatus({res:true, msg:"La richiesta è stata inserita. Riceverà una mail di conferma all'indirizzo indicato"});
        scrollToTop();
        setFieldValue('recaptcha', '');
      }
      setFieldValue('recaptcha', '');
      setSubmitting(false);
    })
    .catch(error => {
      setSubmitting(false);
      var errmsg = "Si è verificato un problema durante l'inserimento della richiesta. ";
      if(error.response && error.response.data.error){
        errmsg += " " + error.response.data.error; 
      }else{
        errmsg += " Servizio non disponibile"
      }
      setStatus({res: false, msg: errmsg});
      scrollToTop();
      setFieldValue('recaptcha', '');
    });
  }      
  ,
  displayName: 'BasicForm', // helps with React DevTools
})(MyInnerForm);
export default ModuloIscrizione;
